/**
 * Utility to parse Controller Response to get required info for
 * Answer Page display.
 */
const AnswerUtil = {

  /**
   * Given a Raw Controller Response that has a 'success' response, normalizes
   * overall (combinator) confidences and individual (solver) confidences
   * across different answer options.
   * Sample incoming Controller Response snippet:
   *  {
   *    "response": {
   *      "success": {
   *        "answers": [{
   *          "selection": {
   *            "key": "(D)",
   *            "assertion": "Is it true that During winter a rabbit ' s fur would be thickest?",
   *            "focus": "winter",
   *            "answer": "winter",
   *            "index": 3
   *          },
   *          "confidence": 0.6014152390207058,
   *          "selected": true,
   *          "analyses": [{
   *            "component": {
   *              "name": "Lucience",
   *               "version": {
   *                 "git": "fb01fac92a10bfb5446ccb57998a346e04cff377",
   *                  "commitDate": 1443036705000,
   *                  "artifact": "0.0.4-SNAPSHOT",
   *                  "cacheKey": "7cd030e6a0267cca730e4b427f669b405f3cc1e3"
   *               }
   *            },
   *            "confidence": 2.6086736490083546,
   *            "analysis": {
   *              "sourceFriendlyName": "Barrons 4th Grade Study Guide",
   *              "lucienceScore": 2.6086736490083546,
   *              "source": "Barrons.sentences.txt",
   *              "query": "winter thickest year season fur rabbit",
   *              "explanation": "A bear's fur would be thickest during winter."
   *            },
   *            "numericFeatures": {
   *
   *            }
   *         },
   *           ...
   *         ]
   *       }
   *       ...
   *       ]
   *     }
   *     "trail": {
   *       "crumb": {
   *         "componentName": "Controller",
   *         "version": {
   *           "git": "458871ec7cf17ebd330e08211554f6fe8040099a",
   *           "commitDate": 1567221114000,
   *           "artifact": "0.0.9-SNAPSHOT",
   *           "cacheKey": "405eefd918abef47f21ec677d8b39dac8403c277"
   *         },
   *         "elapsedTime": 8103,
   *         "extra": { }
   *       },
   *       "children": [
   *         {
   *           "crumb": {
   *             "componentName": "Lucience",
   *             "version": {
   *               "git": ...,
   *               "commitDate": ...,
   *               "artifact": ...,
   *               "cacheKey": ...
   *             },
   *             "elapsedTime": 8096,
   *             extra: { }
   *           }
   *         },
   *        ...
   *       ]
   *     }
   *   }
   */
  getControllerResponseWithNormalizedScores(rawControllerResponse) {
    let updatedControllerResponse = JSON.parse(JSON.stringify(rawControllerResponse));
    const successResponse = updatedControllerResponse.response.success;
    if (successResponse) {

      // Helper function to determine sum of confidences.
      const add = (a, b) => a + b;

      // Helper function to get calibrated score in an analysis if available,
      // otherwise default to confidence.
      const getCalibratedScore = analysis => {
        const secondaryFeatures = analysis.analysis.secondaryFeatures;
        return (secondaryFeatures && secondaryFeatures.calibratedScore ? secondaryFeatures.calibratedScore :
          analysis.confidence);
      };

      // 1. Normalize individual solver confidences over answer choices.
      // Get Solver Confidences for all answer choices to determine sum to normalize
      // per answer choice confidence over.
      let perSolverAnswerChoiceConfidences = {};
      let perSolverAnswerChoiceConfidencesSum = {};
      successResponse.answers.forEach((answer) => {
          answer.analyses.forEach((analysis) => {
            const solverName = analysis.component.name;
            const solverConfidence = getCalibratedScore(analysis) || 0.0;
            if(!(solverName in perSolverAnswerChoiceConfidences)) {
              perSolverAnswerChoiceConfidences[solverName] = [];
            }
            perSolverAnswerChoiceConfidences[solverName].push(solverConfidence);
          });
      });
      // Determine sum of confidence values for all answer choices per solver.
      // This is non-zero to ensure we don't have a divide-by-zero situation when
      // normalizing.
      const initialSum = 0.000001;
      for (var solverName in perSolverAnswerChoiceConfidences) {
        const solverConfidences = perSolverAnswerChoiceConfidences[solverName];
        const solverConfidencesSum = solverConfidences.reduce(add, initialSum);
        perSolverAnswerChoiceConfidencesSum[solverName] = solverConfidencesSum;
      }
      // Normalize answer choice confidences for all solvers.
      successResponse.answers.forEach((answer) => {
          answer.analyses.forEach((analysis) => {
            const normalizedSolverConfidence =
              (getCalibratedScore(analysis) || 0.0) / perSolverAnswerChoiceConfidencesSum[analysis.component.name];
            analysis.confidence = normalizedSolverConfidence;
          });
      });

      // 2. Normalize overall (combined) confidence over answer choices.
      const answerConfidences = successResponse.answers.map(function(answer) {
          return answer.confidence;
      });
      const answerConfidencesSum = answerConfidences.reduce(add, initialSum);

      successResponse.answers.forEach((answer) => {
          answer.confidence /= answerConfidencesSum;
      });
    }
    return updatedControllerResponse;
  },

  /**
   * Given a success controller response, returns a selected answer and a list of
   * unselected answers based on Confidence and the Boolean 'selected' flag.
   * Input shape: the `success` part of the response in the above
   * Raw Controller Response snippet.
   */
  selectedAndUnselectedAnswers(controllerResponse) {
    // Results to return.
    let answerResults = { selectedAnswer: undefined, unselectedAnswers: [] };

    // Sort function, for highest-confidence first.
    const byConfidence = function(a, b) { return b.confidence - a.confidence; };

    // Sort answers by confidence.
    const sortedAnswers = controllerResponse.answers.sort(byConfidence);

    // Get selected & unselected answers from sorted answer list.
    sortedAnswers.forEach((answer) => {
      // Pick the first (highest-confidence) answer that is marked as "selected".
      if ((answerResults.selectedAnswer === undefined) && answer.selected) {
        answerResults.selectedAnswer = answer;
      } else {
        answerResults.unselectedAnswers.push(answer);
      }
    });
    return answerResults;
  },

  /**
   * Hacky function to decide from returned answer options whether the questions was a
   * yes/no or true/false question
   */
  isTrueFalseMCQuestion(answer) {
    const tfWords = ["yes", "no", "true", "false"];
    const selections = [answer.selectedAnswer, ...answer.unselectedAnswers];
    let res = false;
    selections.map((x) => {
      if (tfWords.indexOf(x.selection.multipleChoice.focus.toLowerCase()) > -1) {
        res = true;
      }
    });
    return res;
  },

  /**
   * Returns the list of answer analyses for which we want to show returned explanations.
   * For now, this prefers a Concmod explanation if there is one. Otherwise return all
   * Lucience explanations.
   * Takes an answer and returns the filtered list of explanations.
   * The input 'answer' here is one of the 'answers' array items in the controller
   * response structure shown at the top of the `selectedAndUnselectedAnswers` function.
   */
  bestAnalyses(answer) {
    let concmodRes = null;
    answer.analyses.forEach((analysis) => {
      if (analysis.component.name == "Concmod" && analysis.analysis.concmodExplanationHTML) {
        concmodRes = analysis;
      }
    });
    if (concmodRes === null) {
      return(answer.analyses.filter(analysis => (analysis.component.name === "Lucience")));
    }
    return([concmodRes]);
  },

  /**
   * Pick out analyses whose solver names are listed in solvers (relying on lower case matching)
   */
  solverAnalyses(answer, solvers) {
    let analyses = [];
    solvers.map((solver) => {
      let res;
      answer.analyses.forEach((analysis) => {
        if (analysis.component.name.toLowerCase() == solver.solver) {res = analysis}
      });
      if (res) {analyses.push(Object.assign({analysis: res}, solver))}
    });
    return analyses;
  },

  /**
   * Formats a given answer confidence value (double) as a percentage
   * with specified number of decimal places for display.
   */
  scoreAsPercentage(score, decimals) {
      const percent = score * 100;
      return parseFloat(percent).toFixed(decimals) + '%';
  },

  /**
    * Returns the explanation text for a solver answer
    * Lucene stores the text under "explanation"; others use "demoExplanationText"
  */
  getExplanationText(analysis) {
    return analysis.analysis.explanation || analysis.analysis.demoExplanationText;
  },

  /**
   * Formats a given multiple choice answer label by enclosing in parens.
   */
  formatMCChoiceLabel(label) {
      return "(" + label + ")";
  },
}

export default AnswerUtil;
